// Case content search API.
//
// Two endpoints:
//   * POST /v1/cases/{caseId}/search/content — grouped browse + search.
//     Empty query = browse mode (per-type previews ordered by mention count).
//   * GET  /v1/cases/{caseId}/search/entities/{typeLabel} — paginated list
//     used by the per-type drill-in page.
//
// Both take a document: only what that document yielded.

import { emptyViewState, type GraphView } from '@/types/graph-view'
import { getEntityDisplayLabel } from '@/utils/entity-display'
import { apiClient } from './client'

export interface EntityHit {
  entity_id: string
  entity_label: string
  entity_name: string | null
  entity_props: Record<string, unknown>
  match_field: string
  match_value: string
  score: number
  document_id: string | null
  document_name: string | null
  chunk_id: string | null
  page_number: number | null
}

export interface ChunkHit {
  chunk_id: string
  document_id: string | null
  document_name: string | null
  page_number: number | null
  section: string | null
  snippet: string
  score: number
}

export interface DocumentHit {
  document_id: string
  name: string | null
  filename: string | null
  file_type: string | null
  match_field: string
  score: number
}

export interface EntityGroupPreview {
  type: string
  total: number
  preview: EntityHit[]
}

export interface SearchContentResponse {
  entities: EntityHit[]
  chunks: ChunkHit[]
  documents: DocumentHit[]
  /** Per-entity-type grouped view powering the Research browse/search grid. */
  groups: EntityGroupPreview[]
}

export interface SearchContentOptions {
  limitEntities?: number
  limitChunks?: number
  limitDocuments?: number
  perTypePreviewLimit?: number
  /** Only what this document yielded. */
  documentId?: string
}

/**
 * Search case content.
 *
 * - Pass an empty query for browse mode (returns per-type previews sorted by
 *   live mention count, with `groups` populated and `entities` mirroring the
 *   concatenation of previews for legacy consumers).
 * - Pass a non-empty query for the standard fuzzy-search flow (entities are
 *   scored across name-like properties; results are also grouped by type).
 */
export function searchCaseContent(
  caseId: string,
  query: string,
  options: SearchContentOptions = {}
): Promise<SearchContentResponse> {
  return apiClient.post<SearchContentResponse>(`/cases/${caseId}/search/content`, {
    query,
    limit_entities: options.limitEntities ?? 8,
    limit_chunks: options.limitChunks ?? 8,
    limit_documents: options.limitDocuments ?? 8,
    per_type_preview_limit: options.perTypePreviewLimit ?? 4,
    document_id: options.documentId,
  })
}

// ─── Per-type drill-in list endpoint ─────────────────────────────────────────

export type EntityListSort = 'mentions' | 'name' | 'score'
export type EntityListOrder = 'asc' | 'desc'

export interface EntityListPage {
  type: string
  total: number
  items: EntityHit[]
  next_cursor: string | null
}

export interface EntityListParams {
  query?: string
  sort?: EntityListSort
  order?: EntityListOrder
  cursor?: string
  limit?: number
  documentId?: string
}

/**
 * Resolve the display label for an `EntityHit` using the canonical
 * fallback chain (`@/utils/entity-display`).
 *
 * Priority: schema-driven `_display_name` stamp (resolved on the backend
 * and surfaced as `entity_name`) → literal `name` → per-type priority
 * props (PhoneNumber → `e164`, Vehicle → `plate`, BankAccount → `iban`,
 * …) → generic name-ish keys → `<EntityType> <short id>` last resort.
 *
 * Use this everywhere the UI renders an entity from a Research hit so the
 * label honours the schema's `is_display_name` markers instead of falling
 * back to a bare element id when `name` is empty.
 */
export function getEntityHitDisplayName(hit: EntityHit): string {
  return getEntityDisplayLabel({
    id: hit.entity_id,
    name: hit.entity_name ?? undefined,
    typeLabel: hit.entity_label,
    properties: hit.entity_props,
  })
}

/**
 * Project a slice of `EntityHit`s onto the `GraphView` shape consumed by
 * `addContributionToActive` / `openAsExploration`. Used by both the bulk
 * action on the per-type list page and the single-entity "Add to graph"
 * action in the detail sheet — keeps the projection (and the display-name
 * resolution) in one place.
 */
export function buildGraphViewFromEntityHits(
  hits: EntityHit[],
  caseId: string,
  label: string
): GraphView {
  return {
    meta: {
      source: 'chips',
      createdAt: new Date().toISOString(),
      caseId,
      label,
    },
    nodes: hits.map(h => ({
      id: h.entity_id,
      labels: [h.entity_label],
      typeLabel: h.entity_label,
      name: getEntityHitDisplayName(h),
      properties: h.entity_props ?? {},
    })),
    edges: [],
    view: emptyViewState(),
  }
}

export function listCaseEntitiesByType(
  caseId: string,
  typeLabel: string,
  params: EntityListParams = {}
): Promise<EntityListPage> {
  return apiClient.get<EntityListPage>(
    `/cases/${caseId}/search/entities/${encodeURIComponent(typeLabel)}`,
    {
      params: {
        query: params.query || undefined,
        sort: params.sort,
        order: params.order,
        cursor: params.cursor,
        limit: params.limit,
        document_id: params.documentId,
      },
    }
  )
}
